import { useEffect, useMemo } from "react";
import { useForm, useWatch } from "react-hook-form";
import {
  BUTTON_LABELS,
  CONFIG_PAGES,
  PERMISSIONS,
} from "@miplatform/constants";
import { useRouter } from "@miplatform/hooks";
import { IUserResponse } from "@miplatform/types";
import { zodResolver } from "@hookform/resolvers/zod";
import { usePermissions } from "@mibuildweb/hooks";
import { Typography } from "@mui/material";
import { UserDepartmentsTabView } from "./tabs/UserDepartmentsTabView";
import { UserDetailsTabView } from "./tabs/UserDetailsTabView";
import { UserRolesTabView } from "./tabs/UserRolesTabView";
import { VerticalTabsLayout } from "../../../../components";
import { CONFIG_PATHS } from "../../../../routes/config/constants";
import { ConfigPageLayout } from "../../ConfigPageLayout";
import { ContactMethod, IUserFormData } from "../ConfigUsers.types";
import { configUserSchema } from "../schemas/ConfigUser.schema";
import { useUserDetailsViewModel } from "./useUserDetailsViewModel";

interface IUsersDetailsViewModelProps {
  getUserDetails: (id: string) => void;
  handleCreateUser: (data: IUserFormData) => void;
  handleUpdateUser: (data: IUserFormData) => void;
  userDetails: IUserResponse | Record<string, never> | undefined;
  profilePictureSrc: string | null | undefined;
  setPendingProfilePicture: (file: File) => void;
  pendingProfilePicture: File | null;
  isLoading: boolean;
}

const defaultUserData: IUserFormData = {
  firstName: "",
  lastName: "",
  displayName: "",
  userName: "",
  primaryPhoneNumber: "",
  email: "",
  preferredContactMethod: ContactMethod.Email,
  primaryLocation: "",
  timezone: "",
  language: "",
};

export function ConfigUserDetails() {
  const { goBack, params, pathname } = useRouter();
  const { id, userId: departmentUserId } = params;

  const { hasPermission } = usePermissions();
  const isReadOnly = !hasPermission(PERMISSIONS.USER?.UPDATE);

  const isViewingDepartmentUser = pathname.includes(
    CONFIG_PATHS[CONFIG_PAGES.DEPARTMENTS]
  );

  const userId = departmentUserId || id;

  const form = useForm<IUserFormData>({
    mode: "onChange",
    defaultValues: defaultUserData,
    resolver: zodResolver(configUserSchema),
  });

  const {
    control,
    formState: { isValid, isDirty },
    getValues,
    reset,
  } = form;

  const {
    getUserDetails,
    handleCreateUser,
    handleUpdateUser,
    profilePictureSrc,
    setPendingProfilePicture,
    pendingProfilePicture,
    isLoading,
  }: IUsersDetailsViewModelProps = useUserDetailsViewModel({
    reset,
  });

  const departmentsCount = useWatch({
    control: form.control,
    compute: (data) => data.departments?.length || 0,
  });

  const selectedDepartments = useWatch({
    control: form.control,
    name: "departments",
  });

  const selectedRoles = useWatch({
    control: form.control,
    name: "roles",
  });

  const handleSaveUser = async () => {
    const data = getValues();
    if (userId) {
      handleUpdateUser(data);
    } else {
      handleCreateUser(data);
    }
  };

  const canSaveUser = () => {
    if (isLoading) return false;
    if (isReadOnly) return false;
    if (!isValid) return false;
    if (!isDirty) {
      if (pendingProfilePicture) {
        return true;
      }
      return false;
    }
    return true;
  };

  const userActions = isReadOnly
    ? null
    : {
        cancel: {
          label: BUTTON_LABELS.CANCEL,
          onClick: () => {
            reset();
            goBack();
          },
        },
        submit: {
          label: "Save User",
          onClick: handleSaveUser,
          disabled: !canSaveUser(),
        },
      };

  const watchDisplayName = useWatch({
    control,
    name: "displayName",
  });

  const isCreatingNewUser = !userId;

  const renderPageTitle = () => {
    const action = isCreatingNewUser ? "Create" : "Edit";
    const userLabel = watchDisplayName ? `${watchDisplayName}` : "New User";
    return (
      <Typography variant="HD4">
        {action} User:{" "}
        <Typography variant="HD4" color="primary" component="span">
          {userLabel}
        </Typography>
      </Typography>
    );
  };

  const tabs = useMemo(
    () => [
      {
        label: "User Details",
        value: "user details",
        controlledFormViewComponent: UserDetailsTabView,
        isComplete: isValid,
        hasErrors: !isValid,
        extraTabProps: {
          profilePictureSrc,
          setPendingProfilePicture,
          isReadOnly,
          isCreatingNewUser,
        },
      },
      {
        label: "Departments",
        value: "departments",
        isComplete: departmentsCount > 0,
        controlledFormViewComponent: UserDepartmentsTabView,
        extraTabProps: { isViewingDepartmentUser, isReadOnly },
      },
      {
        label: "Roles",
        value: "roles",
        controlledFormViewComponent: UserRolesTabView,
        extraTabProps: {
          departments: selectedDepartments,
          isReadOnly,
          selectedRoles,
        },
        isComplete: selectedRoles && selectedRoles.length > 0,
      },
    ],
    [
      isValid,
      profilePictureSrc,
      pendingProfilePicture,
      departmentsCount,
      selectedDepartments,
      selectedRoles,
      watchDisplayName,
      isReadOnly,
      isViewingDepartmentUser,
    ]
  );

  useEffect(() => {
    if (userId) {
      getUserDetails(userId);
    }
  }, [userId]);

  return (
    <ConfigPageLayout
      renderTitle={renderPageTitle}
      bottomActions={userActions}
      loading={isLoading}
    >
      <VerticalTabsLayout tabs={tabs} form={form} transparentBackground />
    </ConfigPageLayout>
  );
}
